<script setup lang="ts">
import { ref, onUnmounted, watch } from 'vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../configs/text';
import AudioRecorderButton from './AudioRecorderButton.vue';
import { audioManager } from '../../../utils/audioManager';

const blob = defineModel<Blob | null>()
const { disabled = false, submitted = false } = defineProps<{ disabled?: boolean, submitted?: boolean }>()

const isRecording = ref<boolean>(false)
const recordedChunks: Blob[] = []
const mediaRecorder = ref<MediaRecorder | null>(null)
let mediaStream: MediaStream | null = null
let isDiscardData: boolean = false
const isError = ref<boolean>(false)
const maxDuration = 15000
const stopTimestamp = ref<number>(0)
const recordingRemaining = ref<number>(0)
let stopTimeout: NodeJS.Timeout | null = null
const audioKey = 'vocab'
const peakSize = 15
const recordingPeak = ref<number[]>(Array(peakSize).fill(0))
let animRequest: number = 0
const emit = defineEmits(['submit', 'retry'])

async function startRecording() {
    if (disabled) return;

    try {
        // Request access to the microphone
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        isRecording.value = true

        // Set up MediaRecorder
        mediaRecorder.value = new MediaRecorder(mediaStream, { audioBitsPerSecond: 32000 })
        mediaRecorder.value.ondataavailable = (event) => {
            if (event.data.size > 0) {
                recordedChunks.push(event.data)
            }
        }

        mediaRecorder.value.onstop = () => {
            if (isDiscardData) return;

            blob.value = new Blob(recordedChunks, { type: 'audio/webm' })
            recordedChunks.length = 0 // Clear the recorded chunks
        }

        mediaRecorder.value.start()
        isError.value = false

        // Create an audio context and connect the stream source to an analyzer node
        const context = new AudioContext()
        const source = context.createMediaStreamSource(mediaStream)
        const analyzer = context.createAnalyser()
        source.connect(analyzer)

        // The array we will put sound wave data in
        const array = new Uint8Array(analyzer.fftSize);

        const getPeakLevel = () => {
            // Increase to boost visual impact
            const amplification = 5

            analyzer.getByteTimeDomainData(array);
            const chunkSize = Math.floor(array.length / peakSize);
            const peaks = [];

            for (let i = 0; i < peakSize; i++) {
                let sum = 0;

                for (let j = 0; j < chunkSize; j++) {
                    const index = i * chunkSize + j;
                    sum += Math.abs(array[index] - 128); // Centered on zero
                }

                const avg = sum / chunkSize;
                let normalized = avg / 128; // Normalize to 0–1

                normalized *= amplification;
                normalized = Math.min(normalized, 1); // Clamp to 1

                peaks.push(normalized);
            }

            return peaks;
        }
        const calculateRecordingRemaining = (): number => {
            const currentTimestamp = Date.now()
            if (stopTimestamp.value - currentTimestamp > 0) {
                return Math.ceil((stopTimestamp.value - currentTimestamp) / 1000)
            }
            return 0
        }
        const tick = () => {
            recordingPeak.value = getPeakLevel()
            recordingRemaining.value = calculateRecordingRemaining()
            animRequest = requestAnimationFrame(tick)
        }
        tick()

        // Set a timeout to stop recording after max duration
        stopTimeout = setTimeout(() => stopRecording(), maxDuration)
        stopTimestamp.value = Date.now() + maxDuration

    } catch (error) {
        console.error('Error accessing microphone:', error)
        stopRecording() // Ensure cleanup on error
        isError.value = true
    }
}

function stopRecording() {
    isRecording.value = false

    if (stopTimeout) {
        clearTimeout(stopTimeout)
    }

    if (mediaRecorder.value) {
        mediaRecorder.value.stop()

        // Discard recorded data if specified
        if (isDiscardData) {
            recordedChunks.length = 0
        }
    }

    // Stop all tracks in the media stream
    if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop())
        mediaStream = null
    }

    // stop analyzer anim
    if (animRequest) {
        cancelAnimationFrame(animRequest)
    }

    // reset timer
    recordingRemaining.value = 0
}

function restartRecording() {
    stopRecording()
    blob.value = null
}

function playRecording() {
    if (!blob.value) return;
    const audioUrl = URL.createObjectURL(blob.value)
    const audio = audioManager.play(audioUrl, audioKey)
    audio.ontimeupdate = () => {
        recordingRemaining.value = Math.ceil(audio.currentTime)
    }
    audio.onended = () => {
        recordingRemaining.value = 0
    }
}

function stopPlayback() {
    audioManager.stop(audioKey)
    recordingRemaining.value = 0
}

watch(blob, () => {
    if (blob.value) return;
    audioManager.stop(audioKey)
})

// Cleanup when the component unmounts
onUnmounted(() => {
    isDiscardData = true
    stopRecording()
})

const { t } = useI18n()
</script>

<template>
    <div class="w-full flex flex-col items-center text-center">
        <!-- disabled -->
        <template v-if="disabled">
            <AudioRecorderButton state="disabled" />
            <div class="font-bold text-red-600 mt-1">{{ t(TEXT.recording_ended) }}</div>
        </template>
        <template v-else>
            <template v-if="!isRecording">
                <!-- no record -->
                <template v-if="!blob">
                    <AudioRecorderButton state="ready" @click="startRecording" />
                    <!-- error -->
                    <div v-if="isError" class="text-red-600 font-bold mt-2 text-lg">{{ t(TEXT.no_mic_found) }}</div>
                </template>
                <!-- recorded -->
                <template v-else>
                    <AudioRecorderButton v-if="!submitted" state="recorded" :peak="recordingPeak" :remaining="recordingRemaining"
                        @click="emit('submit')" @play="playRecording" @stop="stopPlayback" @restart="restartRecording" />
                    <AudioRecorderButton v-else state="retry" @click="restartRecording" />
                </template>
            </template>
            <template v-else>
                <!-- recording -->
                <AudioRecorderButton state="recording" :peak="recordingPeak" :remaining="recordingRemaining"
                    @click="stopRecording" />
            </template>
        </template>
    </div>
</template>
